/** localStorage key holding the dismissal timestamp (epoch ms). */
export const mobileAppBannerDismissedKey = "mentor-me:mobile-app-banner:dismissed-at";

/** How long a dismissal hides the banner. */
export const mobileAppBannerDismissDurationMs = 14 * 24 * 60 * 60 * 1000;

type DismissalStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function browserStorage(): DismissalStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Whether the banner was dismissed during the last 14 days. Unreadable or
 * malformed storage is treated as "not dismissed".
 *
 * @example
 * if (!isMobileAppBannerDismissed()) showBanner();
 */
export function isMobileAppBannerDismissed(
  now = Date.now(),
  storage: DismissalStorage | null = browserStorage(),
) {
  try {
    const dismissedAt = Number(storage?.getItem(mobileAppBannerDismissedKey));

    if (!Number.isFinite(dismissedAt) || dismissedAt <= 0) return false;

    if (now - dismissedAt < mobileAppBannerDismissDurationMs) return true;

    storage?.removeItem(mobileAppBannerDismissedKey);
    return false;
  } catch {
    return false;
  }
}

/**
 * Hide the banner for the next 14 days. Storage failures are ignored; the
 * banner then stays hidden only for the current page lifetime.
 *
 * @example
 * dismissMobileAppBanner();
 */
export function dismissMobileAppBanner(
  now = Date.now(),
  storage: DismissalStorage | null = browserStorage(),
) {
  try {
    storage?.setItem(mobileAppBannerDismissedKey, String(now));
  } catch {
    // Private mode / blocked storage: in-memory dismissal only.
  }
}
